import React, { useState, useRef, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { PiClockCountdownFill } from 'react-icons/pi';
import { FaFlag, FaChevronLeft, FaChevronRight, FaCheck, FaRedo, FaShieldAlt, FaExclamationTriangle } from 'react-icons/fa';
import { MdFullscreen, MdScreenShare } from 'react-icons/md';

import { formatLargeText } from '../../utils/client-functions';
import useProctoring from '../../hooks/useProctoring';
import { SPECIAL_SYMBOLS } from '../../utils/specialSymbols';

// ─── Styled Components ────────────────────────────────────────────────────────

const pulse = keyframes`0%,100%{opacity:1}50%{opacity:.45}`;

const Overlay = styled.div`
  position: fixed; inset: 0; z-index: 1200;
  background: #f4f7f8;
  display: flex; flex-direction: column;
  font-family: 'Inter', system-ui, sans-serif;
`;

const Header = styled.header`
  position: sticky; top: 0; z-index: 10;
  background: rgba(255,255,255,0.92); backdrop-filter: blur(10px);
  border-bottom: 1px solid #e0eaec;
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 24px;
  gap: 12px;
`;

const ExamTitle = styled.h1`
  margin: 0; font-size: 1rem; font-weight: 700; color: #0c1e28;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis; max-width: 260px;
`;

const TimerBadge = styled.div`
  display: flex; align-items: center; gap: 6px;
  padding: 5px 14px; border-radius: 99px; font-weight: 700; font-size: 0.92rem;
  font-variant-numeric: tabular-nums;
  background: ${p => p.$low ? 'rgba(220,38,38,0.08)' : 'rgba(95,190,197,0.10)'};
  border: 1.5px solid ${p => p.$low ? '#dc2626' : '#5fbec5'};
  color: ${p => p.$low ? '#dc2626' : '#0c7c84'};
  animation: ${p => p.$low ? pulse : 'none'} 1s ease-in-out infinite;
`;

const Body = styled.div`
  flex: 1; overflow-y: auto;
  padding: 28px 24px; max-width: 840px; margin: 0 auto; width: 100%;
`;

const QuestionCard = styled.div`
  background: #fff; border-radius: 16px;
  border: 1px solid #e0eaec; padding: 28px;
  margin-bottom: 20px;
  box-shadow: 0 2px 12px rgba(0,0,0,0.04);
`;

const QuestionMeta = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  margin-bottom: 14px;
`;

const QNum = styled.span`
  font-size: 0.78rem; font-weight: 600; color: #90a4ae; letter-spacing: .5px;
`;

const FlagBtn = styled.button`
  background: none; border: none; cursor: pointer; font-size: 0.85rem;
  display: flex; align-items: center; gap: 5px; padding: 4px 10px;
  border-radius: 8px; transition: all .15s;
  color: ${p => p.$flagged ? '#f59e0b' : '#90a4ae'};
  background: ${p => p.$flagged ? 'rgba(245,158,11,0.10)' : 'transparent'};
  &:hover { background: rgba(245,158,11,0.12); color: #f59e0b; }
`;

const QuestionText = styled.p`
  font-size: 1rem; font-weight: 600; color: #0c1e28; line-height: 1.6; margin: 0 0 22px;
`;

const OptionsList = styled.div`
  display: flex; flex-direction: column; gap: 10px;
`;

const OptionBtn = styled.button`
  width: 100%; text-align: left; padding: 12px 16px;
  border-radius: 10px; border: 1.5px solid;
  border-color: ${p => p.$selected ? '#5fbec5' : '#e0eaec'};
  background: ${p => p.$selected ? 'rgba(95,190,197,0.10)' : '#fafcfc'};
  color: ${p => p.$selected ? '#0c7c84' : '#0c1e28'};
  font-size: 0.93rem; font-weight: ${p => p.$selected ? 700 : 500};
  cursor: pointer; transition: all .15s;
  &:hover { border-color: #5fbec5; background: rgba(95,190,197,0.07); }
`;

const Footer = styled.div`
  background: #fff; border-top: 1px solid #e0eaec;
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 24px; gap: 12px;
`;

const NavBtn = styled.button`
  display: flex; align-items: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; border: none; cursor: pointer;
  font-size: 0.88rem; font-weight: 600; transition: all .15s;
  background: ${p => p.$primary ? '#0c7c84' : '#f0f4f6'};
  color: ${p => p.$primary ? '#fff' : '#0c1e28'};
  &:hover { opacity: .88; }
  &:disabled { opacity: .38; cursor: not-allowed; }
`;

const NavGrid = styled.div`
  display: flex; flex-wrap: wrap; gap: 6px; justify-content: center;
  padding: 0 24px 20px; max-width: 840px; margin: 0 auto; width: 100%;
`;

const NavDot = styled.button`
  width: 36px; height: 36px; border-radius: 8px; border: 1.5px solid;
  font-size: 0.78rem; font-weight: 700; cursor: pointer; transition: all .15s;
  border-color: ${p => p.$active ? '#5fbec5' : p.$answered ? '#10b981' : p.$flagged ? '#f59e0b' : '#dde4e8'};
  background: ${p => p.$active ? 'rgba(95,190,197,0.15)' : p.$answered ? 'rgba(16,185,129,0.10)' : p.$flagged ? 'rgba(245,158,11,0.10)' : '#fafcfc'};
  color: ${p => p.$active ? '#0c7c84' : p.$answered ? '#065f46' : p.$flagged ? '#92400e' : '#607d8b'};
  &:hover { border-color: #5fbec5; }
`;

const SubmitBtn = styled.button`
  padding: 10px 22px; border-radius: 10px; border: none; cursor: pointer;
  font-size: 0.9rem; font-weight: 700; background: #5fbec5; color: #fff;
  transition: all .15s;
  &:hover { background: #0c7c84; }
  &:disabled { opacity: .5; cursor: not-allowed; }
`;

const TheoryAnswerWrap = styled.div`
  display: flex; flex-direction: column; gap: 6px;
`;

const TheoryTextarea = styled.textarea`
  width: 100%; min-height: 160px; padding: 14px 16px;
  border-radius: 10px; border: 1.5px solid ${p => p.$hasValue ? '#10b981' : '#e0eaec'};
  background: ${p => p.$hasValue ? 'rgba(16,185,129,0.04)' : '#fafcfc'};
  font-size: 0.93rem; line-height: 1.6; color: #0c1e28;
  resize: vertical; font-family: inherit;
  transition: border-color .15s;
  &:focus { outline: none; border-color: #5fbec5; }
`;

const TheoryWordCount = styled.div`
  font-size: 0.72rem; color: #90a4ae; text-align: right;
`;

const TheoryTypeBadge = styled.div`
  display: inline-flex; align-items: center; gap: 5px;
  background: rgba(139,92,246,0.10); border: 1px solid rgba(139,92,246,0.30);
  color: #7c3aed; border-radius: 99px; padding: 2px 10px;
  font-size: 0.7rem; font-weight: 700; letter-spacing: .4px; margin-bottom: 10px;
`;

// ─── Confirm Dialog ───────────────────────────────────────────────────────────

const ModalBack = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.45); z-index: 1300;
  display: flex; align-items: center; justify-content: center;
`;

const Dialog = styled.div`
  background: #fff; border-radius: 18px; padding: 32px 28px;
  width: min(94vw, 420px); box-shadow: 0 20px 60px rgba(0,0,0,0.18);
`;

const DialogTitle = styled.h3`margin: 0 0 10px; color: #0c1e28; font-size: 1.1rem;`;
const DialogSub = styled.p`margin: 0 0 22px; color: #607d8b; font-size: 0.88rem; line-height: 1.5;`;

const DialogBtns = styled.div`display:flex;gap:10px;justify-content:flex-end;`;
const DBtn = styled.button`
  padding: 9px 20px; border-radius: 9px; border: none; cursor: pointer;
  font-size: 0.88rem; font-weight: 600;
  background: ${p => p.$primary ? '#5fbec5' : '#f0f4f6'};
  color: ${p => p.$primary ? '#fff' : '#0c1e28'};
  &:hover { opacity: .88; }
`;

// ─── Grading Overlay ──────────────────────────────────────────────────────────

const spin = keyframes`to{transform:rotate(360deg)}`;
const Spinner = styled.div`
  width: 40px; height: 40px; border-radius: 50%;
  border: 3px solid #e0eaec; border-top-color: #5fbec5;
  animation: ${spin} .7s linear infinite;
`;

const GradingBox = styled.div`
  display: flex; flex-direction: column; align-items: center; gap: 16px;
  padding: 48px 28px; text-align: center;
  background: #fff; border-radius: 18px;
  box-shadow: 0 8px 40px rgba(0,0,0,0.10);
  width: min(94vw, 440px);
`;

const GradingProgressTrack = styled.div`
  width: 100%; height: 6px; background: #e0eaec; border-radius: 99px; overflow: hidden;
`;
const GradingProgressFill = styled.div`
  height: 100%; border-radius: 99px;
  background: linear-gradient(90deg, #5fbec5, #0c7c84);
  transition: width 0.5s ease-out;
  width: ${p => p.$pct}%;
`;

const DiagramWrapper = styled.div`
  border-radius: 12px; overflow: hidden; margin-bottom: 20px;
  border: 1px solid rgba(95,190,197,0.2); background: #fff;
  img { width: 100%; display: block; max-height: 400px; object-fit: contain; }
`;

const InstructionBlock = styled.div`
  background: rgba(95,190,197,0.07); border-left: 3px solid #5fbec5;
  border-radius: 0 8px 8px 0; padding: 12px 16px; margin-bottom: 18px;
  font-size: 0.88rem; color: #0c1e28; line-height: 1.6;
`;

// ─── Proctoring UI ────────────────────────────────────────────────────────────

const WARNING_COLORS = {
  1: { bg: 'rgba(245,158,11,0.08)', border: '#f59e0b', text: '#92400e', btn: '#f59e0b' },
  2: { bg: 'rgba(234,88,12,0.08)',  border: '#ea580c', text: '#7c2d12', btn: '#ea580c' },
  3: { bg: 'rgba(220,38,38,0.08)',  border: '#dc2626', text: '#7f1d1d', btn: '#dc2626' },
};

const WarningBanner = styled.div`
  display: flex; align-items: flex-start; gap: 10px;
  padding: 10px 18px; font-size: 0.83rem; font-weight: 600;
  background: ${p => (WARNING_COLORS[p.$level] || WARNING_COLORS[1]).bg};
  border-bottom: 2px solid ${p => (WARNING_COLORS[p.$level] || WARNING_COLORS[1]).border};
  color: ${p => (WARNING_COLORS[p.$level] || WARNING_COLORS[1]).text};
`;

const WarningDismiss = styled.button`
  margin-left: auto; background: none; border: none; cursor: pointer;
  font-size: 0.78rem; font-weight: 700; padding: 0 4px;
  color: ${p => (WARNING_COLORS[p.$level] || WARNING_COLORS[1]).btn};
  &:hover { opacity: .7; }
`;

const ViolationBadge = styled.span`
  display: flex; align-items: center; gap: 4px;
  padding: 3px 9px; border-radius: 99px; font-size: 0.75rem; font-weight: 700;
  background: rgba(220,38,38,0.10); border: 1.5px solid #dc2626; color: #dc2626;
`;

const FullscreenGate = styled.div`
  position: fixed; inset: 0; z-index: 1300;
  background: #0c1e28;
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 20px; padding: 32px;
`;

const FullscreenBtn = styled.button`
  display: flex; align-items: center; gap: 10px;
  padding: 14px 32px; border-radius: 12px; border: none; cursor: pointer;
  font-size: 1rem; font-weight: 700; background: #5fbec5; color: #fff;
  transition: background .15s;
  &:hover { background: #0c7c84; }
`;

// ─── Special Symbols Modal ────────────────────────────────────────────────────

const SymTriggerBtn = styled.button`
  background: none; border: 1px solid #b0bec5; border-radius: 6px;
  padding: 2px 8px; font-size: 0.85rem; cursor: pointer; color: #546e7a;
  line-height: 1.4; transition: border-color .15s, color .15s, background .15s;
  &:hover { border-color: #5fbec5; color: #5fbec5; background: #e0f4f5; }
`;

const SymOverlay = styled.div`
  position: fixed; inset: 0; background: rgba(0,0,0,0.55);
  display: flex; align-items: center; justify-content: center;
  z-index: 5000; padding: 16px;
`;

const SymModal = styled.div`
  background: #fff; border-radius: 14px; width: 100%; max-width: 560px;
  max-height: 86vh; display: flex; flex-direction: column; overflow: hidden;
  box-shadow: 0 20px 60px rgba(0,0,0,0.22);
`;

const SymHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 18px 10px; border-bottom: 1px solid #e8edf2; flex-shrink: 0;
`;

const SymTitle = styled.span`font-size: 0.95rem; font-weight: 700; color: #0c1e28;`;

const SymCloseBtn = styled.button`
  background: none; border: none; font-size: 1rem; cursor: pointer;
  color: #90a4ae; padding: 0 4px; line-height: 1;
  &:hover { color: #263238; }
`;

const SymSearchRow = styled.div`padding: 10px 18px 8px; flex-shrink: 0;`;

const SymSearchInput = styled.input`
  width: 100%; box-sizing: border-box; border: 1px solid #b0bec5; border-radius: 8px;
  padding: 7px 12px; font-size: 0.85rem; font-family: inherit; outline: none;
  transition: border-color .15s;
  &:focus { border-color: #5fbec5; }
`;

const SymCategoryTabs = styled.div`
  display: flex; gap: 6px; padding: 0 18px 8px; flex-wrap: wrap; flex-shrink: 0;
`;

const SymCatTab = styled.button`
  padding: 3px 10px; border-radius: 20px; border: 1px solid #b0bec5;
  background: ${p => p.$active ? '#5fbec5' : '#fff'};
  border-color: ${p => p.$active ? '#5fbec5' : '#b0bec5'};
  color: ${p => p.$active ? '#fff' : '#546e7a'};
  font-size: 0.72rem; font-weight: 600; cursor: pointer;
  transition: all .15s; white-space: nowrap;
  &:hover { border-color: #5fbec5; color: ${p => p.$active ? '#fff' : '#5fbec5'}; }
`;

const SymGrid = styled.div`
  display: grid; grid-template-columns: repeat(auto-fill, minmax(46px, 1fr));
  gap: 6px; padding: 8px 18px 16px; overflow-y: auto; flex: 1;
`;

const SymBtn = styled.button`
  width: 100%; aspect-ratio: 1; border: 1px solid ${p => p.$copied ? '#43a047' : '#e0e7ef'};
  border-radius: 8px; background: ${p => p.$copied ? '#e8f5e9' : '#f9fbfd'};
  font-size: 1.15rem; cursor: pointer; display: flex; align-items: center;
  justify-content: center; transition: all .12s;
  color: ${p => p.$copied ? '#2e7d32' : '#1a2a36'}; line-height: 1;
  &:hover { border-color: #5fbec5; background: #e0f4f5; color: #1a6e73; transform: scale(1.12); }
`;

const SymEmpty = styled.div`
  grid-column: 1 / -1; text-align: center; padding: 24px 0; color: #90a4ae; font-size: 0.82rem;
`;

const SymCopiedMsg = styled.div`
  text-align: center; padding: 6px 18px 10px; font-size: 0.78rem;
  color: #43a047; font-weight: 600; flex-shrink: 0;
`;

function SpecialSymbolsModal({ onInsert, onClose }) {
  const [search, setSearch] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');
  const [copiedSymbol, setCopiedSymbol] = useState(null);

  const categories = React.useMemo(
    () => ['All', ...Array.from(new Set(SPECIAL_SYMBOLS.map(s => s.category)))],
    []
  );

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return SPECIAL_SYMBOLS.filter(s => {
      const matchesSearch = !q || s.name.toLowerCase().includes(q) || s.symbol === search.trim();
      const matchesCategory = activeCategory === 'All' || s.category === activeCategory;
      return matchesSearch && matchesCategory;
    });
  }, [search, activeCategory]);

  const handleSymbol = (s) => {
    navigator.clipboard.writeText(s.symbol).catch(() => {});
    onInsert(s.symbol);
    setCopiedSymbol(s.symbol);
    setTimeout(() => setCopiedSymbol(null), 1200);
  };

  return (
    <SymOverlay onClick={onClose}>
      <SymModal className="BubblePopIn" onClick={e => e.stopPropagation()}>
        <SymHeader>
          <SymTitle>Special Symbols</SymTitle>
          <SymCloseBtn type="button" onClick={onClose}>✕</SymCloseBtn>
        </SymHeader>
        <SymSearchRow>
          <SymSearchInput
            placeholder="Search by name (e.g. alpha, integral, arrow…)"
            value={search}
            onChange={e => setSearch(e.target.value)}
            autoFocus
          />
        </SymSearchRow>
        <SymCategoryTabs>
          {categories.map(cat => (
            <SymCatTab
              key={cat}
              type="button"
              $active={activeCategory === cat}
              onClick={() => setActiveCategory(cat)}
            >
              {cat}
            </SymCatTab>
          ))}
        </SymCategoryTabs>
        <SymGrid>
          {filtered.length === 0
            ? <SymEmpty>No symbols match your search.</SymEmpty>
            : filtered.map((s, i) => (
              <SymBtn
                key={i}
                type="button"
                $copied={copiedSymbol === s.symbol}
                onClick={() => handleSymbol(s)}
                title={s.name}
              >
                {s.symbol}
              </SymBtn>
            ))
          }
        </SymGrid>
        {copiedSymbol && (
          <SymCopiedMsg>"{copiedSymbol}" copied &amp; inserted!</SymCopiedMsg>
        )}
      </SymModal>
    </SymOverlay>
  );
}

/**
 * Props:
 *  exam           – { title, instructions, settings: { duration, proctoring, ... } }
 *  questions      – [{ question_number, question_text, options, topic, subjectSectionIdx? }]
 *  subjectSections – optional [{ subjectId, subjectName, duration }] for multi-subject exams
 *  schoolExamId   – MongoDB _id of the SchoolExam
 *  onClose        – called when student exits without submitting
 *  onGradingComplete – called with { examId, examMetaId, score, percentage, passed }
 */
export default function SchoolExamStarted({ exam, questions = [], subjectSections = [], schoolExamId, attempt = {}, serverNow = null, onClose, onGradingComplete }) {
  const isMultiSubject = subjectSections.length > 0;
  const durationSeconds    = (exam?.settings?.duration || 30) * 60;
  const proctoringSettings = exam?.settings?.proctoring || {};
  const restoredAnswers = {};
  const restoredTimeTaken = {};
  (attempt.existingAnswers || []).forEach(savedAnswer => {
    const questionNumber = Number(savedAnswer.questionNumber);
    if (!Number.isInteger(questionNumber)) return;
    restoredAnswers[questionNumber] = savedAnswer.chosenAnswer || '';
    restoredTimeTaken[questionNumber] = savedAnswer.timeTaken || 0;
  });

  // ── Multi-subject: active subject tab ─────────────────────────────────────
  const restoredSubjectIdx = Number(attempt.activeSubjectIdx);
  const initialActiveSubjectIdx = Number.isInteger(restoredSubjectIdx)
    ? Math.min(Math.max(restoredSubjectIdx, 0), Math.max(subjectSections.length - 1, 0))
    : 0;
  const [activeSubjectIdx, setActiveSubjectIdx] = useState(initialActiveSubjectIdx);

  // Derive question list for the active subject tab
  const visibleQuestions = isMultiSubject
    ? questions.filter(q => (q.subjectSectionIdx ?? 0) === activeSubjectIdx)
    : questions;

  const restoredCurrentIdx = Number(attempt.currentQuestionIndex);
  const initialCurrentIdx = Number.isInteger(restoredCurrentIdx)
    ? Math.min(Math.max(restoredCurrentIdx, 0), Math.max(visibleQuestions.length - 1, 0))
    : 0;
  const [currentIdx, setCurrentIdx]   = useState(initialCurrentIdx);
  const [answers, setAnswers]         = useState(() => restoredAnswers);
  const [flagged, setFlagged]         = useState({});
  const [timeTaken, setTimeTaken]     = useState(() => restoredTimeTaken);
  const [attemptCounts, setAttemptCounts] = useState({});

  // ── Special symbols insertion for theory answers ──────────────────────────
  const [symModalOpen, setSymModalOpen] = useState(false);
  const answerTextareaRef = useRef(null);
  const lastSelRef = useRef({ start: 0, end: 0 });

  // Reset question index when switching subject tabs — skip on initial mount so a restored position isn't wiped out
  const subjectTabMountedRef = useRef(false);
  useEffect(() => {
    if (!subjectTabMountedRef.current) { subjectTabMountedRef.current = true; return; }
    setCurrentIdx(0);
  }, [activeSubjectIdx]);

  // ── Stable ref so useProctoring can call doSubmit without stale closure ──────
  const doSubmitRef = useRef(null);

  const token    = localStorage.getItem('school-token');
  const schoolId = localStorage.getItem('school-id');

  // ── Proctoring ───────────────────────────────────────────────────────────────
  const {
    warningLevel,
    violationCount,
    showWarningBanner,
    dismissWarning,
    requestFullscreen,
    requestScreenCapture,
    screenCaptureActive,
  } = useProctoring({
    schoolExamId,
    proctoringSettings,
    token,
    schoolId,
    // Restores the violation count/warning level from the server so a reload can't
    // reset progress toward the max-violations auto-submit threshold
    initialViolationCount: attempt.proctoring?.violationCount || 0,
    // Instead of submitting immediately, show a 5-second countdown so the student
    // understands why their exam is being submitted and violations can flush first.
    onBlock: () => { setAutoSubmitCountdown(5); },
  });

  // Screen-share gate — shown before the exam when proctoring is enabled
  const supportsDisplayMedia = !!navigator.mediaDevices?.getDisplayMedia;
  const [screenShareDone, setScreenShareDone] = useState(
    !proctoringSettings?.enabled || !supportsDisplayMedia
  );
  const [screenShareDeclined, setScreenShareDeclined] = useState(false);
  const [screenShareWrongSurface, setScreenShareWrongSurface] = useState(false);

  const handleStartScreenShare = useCallback(async () => {
    const result = await requestScreenCapture();
    if (result === true) {
      setScreenShareDone(true);
      setScreenShareDeclined(false);
      setScreenShareWrongSurface(false);
    } else if (result === 'wrong_surface') {
      setScreenShareWrongSurface(true);
      setScreenShareDeclined(false);
    } else {
      setScreenShareDeclined(true);
      setScreenShareWrongSurface(false);
    }
  }, [requestScreenCapture]);

  const handleSkipScreenShare = useCallback(() => {
    setScreenShareDone(true);
  }, []);

  // Fullscreen gate — blocks the question UI until the student enters fullscreen
  const [fullscreenReady, setFullscreenReady] = useState(
    !proctoringSettings?.requireFullscreen || !!document.fullscreenElement
  );
  const handleEnterFullscreen = useCallback(async () => {
    await requestFullscreen();
    setFullscreenReady(true);
  }, [requestFullscreen]);

  // ─── Wall-clock timer (keeps running even when tab is minimized) ─────────────
  const serverNowMs = serverNow ? new Date(serverNow).getTime() : NaN;
  const startedAtMs = attempt.startedAt ? new Date(attempt.startedAt).getTime() : NaN;
  const expiresAtMs = attempt.expiresAt ? new Date(attempt.expiresAt).getTime() : NaN;
  const serverOffsetRef = useRef(Number.isFinite(serverNowMs) ? serverNowMs - Date.now() : 0);
  const endTimeRef  = useRef(
    Number.isFinite(expiresAtMs)
      ? expiresAtMs
      : Number.isFinite(startedAtMs)
        ? startedAtMs + durationSeconds * 1000
        : Date.now() + durationSeconds * 1000
  );
  const [secondsLeft, setSecondsLeft] = useState(durationSeconds);

  useEffect(() => {
    // Recalculate on each tick using Date.now() so minimizing the tab doesn't drift the timer
    const tick = () => {
      const rem = Math.max(0, Math.round((endTimeRef.current - (Date.now() + serverOffsetRef.current)) / 1000));
      setSecondsLeft(rem);
    };
    tick();
    const id = setInterval(tick, 1000);

    // When the tab regains focus, fire an immediate tick so the display catches up instantly
    const onVisibility = () => { if (document.visibilityState === 'visible') tick(); };
    document.addEventListener('visibilitychange', onVisibility);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, []); // intentionally stable – endTimeRef is a ref

  const mins       = Math.floor(secondsLeft / 60);
  const secs       = secondsLeft % 60;
  const timeDisplay = `${String(mins).padStart(2,'0')}:${String(secs).padStart(2,'0')}`;
  const isLowTime  = secondsLeft <= 300;
  const isExpired  = secondsLeft <= 0;

  const autoSubmitRef = useRef(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [submitting, setSubmitting]   = useState(false);
  // When proctoring triggers auto-submit, count down from 5 before actually submitting
  const [autoSubmitCountdown, setAutoSubmitCountdown] = useState(null);
  const [gradingJobId, setGradingJobId] = useState(null);
  const [gradingRetry, setGradingRetry] = useState(0); // bumped to re-trigger SSE effect on retry
  const [gradingMsg, setGradingMsg]   = useState('Submitting your exam…');
  const [gradingPct, setGradingPct]   = useState(0);
  const [gradingError, setGradingError] = useState(null);
  const [resultPending, setResultPending] = useState(null); // { visible: true, message: '...' }

  const q    = visibleQuestions[currentIdx] || {};
  const qNum = q.question_number || currentIdx + 1;

  const answersRef = useRef(restoredAnswers);
  const timeTakenRef = useRef(restoredTimeTaken);
  const attemptCountsRef = useRef({});
  const currentIdxRef = useRef(currentIdx);
  const activeSubjectIdxRef = useRef(activeSubjectIdx);
  const progressVersionRef = useRef(Math.max(0, Number(attempt.progressVersion) || 0));
  const saveTimeoutRef = useRef(null);
  const saveProgressRef = useRef(null);
  const saveDisabledRef = useRef(false);
  const savingRef = useRef(false);
  const mountedRef = useRef(true);
  const [saveState, setSaveState] = useState('idle');

  useEffect(() => { answersRef.current = answers; }, [answers]);
  useEffect(() => { timeTakenRef.current = timeTaken; }, [timeTaken]);
  useEffect(() => { attemptCountsRef.current = attemptCounts; }, [attemptCounts]);
  useEffect(() => { currentIdxRef.current = currentIdx; }, [currentIdx]);
  useEffect(() => { activeSubjectIdxRef.current = activeSubjectIdx; }, [activeSubjectIdx]);

  const qStartRef = useRef(Date.now());
  useEffect(() => { qStartRef.current = Date.now(); }, [currentIdx]);
  useEffect(() => { setSymModalOpen(false); }, [currentIdx, activeSubjectIdx]);

  const recordTime = useCallback((qn, updateState = true) => {
    const elapsed = Math.round((Date.now() - qStartRef.current) / 1000);
    if (elapsed <= 0) return;
    const nextTimeTaken = {
      ...timeTakenRef.current,
      [qn]: (timeTakenRef.current[qn] || 0) + elapsed,
    };
    timeTakenRef.current = nextTimeTaken;
    if (updateState) setTimeTaken(nextTimeTaken);
    qStartRef.current = Date.now();
  }, []);

  const recordActiveQuestionTime = useCallback((updateState = true) => {
    const activeQuestion = visibleQuestions[currentIdxRef.current];
    const activeQuestionNumber = activeQuestion?.question_number;
    if (!activeQuestionNumber) return;
    recordTime(activeQuestionNumber, updateState);
  }, [recordTime, visibleQuestions]);

  const saveProgress = useCallback(async ({ beacon = false } = {}) => {
    if (saveDisabledRef.current || !token) return;
    // Skip overlapping saves — the next scheduled trigger will pick up any newer state
    if (!beacon && savingRef.current) return;

    recordActiveQuestionTime(!beacon);
    const progressVersion = progressVersionRef.current + 1;
    progressVersionRef.current = progressVersion;
    const payload = {
      answers: questions.map(qs => ({
        questionNumber: qs.question_number,
        chosenAnswer: answersRef.current[qs.question_number] || '',
        timeTaken: timeTakenRef.current[qs.question_number] || 0,
      })),
      currentQuestionIndex: currentIdxRef.current,
      activeSubjectIdx: activeSubjectIdxRef.current,
      progressVersion,
      trackAnalytics: progressVersion % 5 === 0,
    };
    const endpoint = `/student/exams/${schoolExamId}/save-progress`;
    const requestBody = JSON.stringify(payload);

    if (beacon) {
      const params = new URLSearchParams({ token });
      if (schoolId) params.set('schoolId', schoolId);
      const beaconUrl = `${endpoint}?${params.toString()}`;
      if (navigator.sendBeacon) {
        navigator.sendBeacon(
          beaconUrl,
          new Blob([requestBody], { type: 'application/json' })
        );
      } else {
        const headers = { 'Content-Type': 'application/json', 'x-access-token': token };
        if (schoolId) headers['x-school-id'] = schoolId;
        fetch(endpoint, { method: 'POST', headers, body: requestBody, keepalive: true }).catch(() => {});
      }
      return;
    }

    savingRef.current = true;
    if (mountedRef.current) setSaveState('saving');
    const headers = { 'Content-Type': 'application/json', 'x-access-token': token };
    if (schoolId) headers['x-school-id'] = schoolId;
    try {
      const res = await fetch(endpoint, { method: 'POST', headers, body: requestBody });
      const data = await res.json();
      if (!res.ok) {
        if (data.expired) {
          saveDisabledRef.current = true;
          if (doSubmitRef.current) doSubmitRef.current({ automatic: true });
          return;
        }
        throw new Error(data.error || 'Unable to save progress');
      }
      if (mountedRef.current) setSaveState('saved');
    } catch {
      if (mountedRef.current) setSaveState('error');
    } finally {
      savingRef.current = false;
    }
  }, [questions, recordActiveQuestionTime, schoolExamId, schoolId, token]);

  useEffect(() => {
    saveProgressRef.current = saveProgress;
  }, [saveProgress]);

  const scheduleAutosave = useCallback(() => {
    if (saveDisabledRef.current) return;
    if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
    saveTimeoutRef.current = setTimeout(() => {
      saveTimeoutRef.current = null;
      if (saveProgressRef.current) saveProgressRef.current();
    }, 2500);
  }, []);

  const goTo = useCallback((idx) => {
    recordTime(qNum);
    currentIdxRef.current = idx;
    setCurrentIdx(idx);
    scheduleAutosave();
  }, [qNum, recordTime, scheduleAutosave]);

  const selectAnswer = useCallback((option) => {
    const prev = answersRef.current[qNum];
    const nextAnswers = { ...answersRef.current, [qNum]: option };
    answersRef.current = nextAnswers;
    setAnswers(nextAnswers);
    if (prev !== option) {
      const nextAttemptCounts = {
        ...attemptCountsRef.current,
        [qNum]: (attemptCountsRef.current[qNum] || 0) + 1,
      };
      attemptCountsRef.current = nextAttemptCounts;
      setAttemptCounts(nextAttemptCounts);
    }
    scheduleAutosave();
  }, [qNum, scheduleAutosave]);

  const toggleFlag = useCallback(() => {
    setFlagged(f => ({ ...f, [qNum]: !f[qNum] }));
  }, [qNum]);

  const doSubmit = useCallback(async ({ automatic = false } = {}) => {
    recordTime(qNum);
    saveDisabledRef.current = true;
    setShowConfirm(false);
    setSubmitting(true);
    setGradingError(null);

    const payload = questions.map(qs => ({
      questionNumber: qs.question_number,
      chosenAnswer:   answersRef.current[qs.question_number] || '',
      timeTaken:      timeTakenRef.current[qs.question_number] || 0,
      attempts:       attemptCountsRef.current[qs.question_number] || 0,
    }));

    const headers = { 'Content-Type': 'application/json', 'x-access-token': token };
    if (schoolId) headers['x-school-id'] = schoolId;

    try {
      const res = await fetch(`/student/exams/${schoolExamId}/submit`, {
        method: 'POST', headers,
        body: JSON.stringify({ answers: payload, autoSubmit: automatic }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (!data.expired) saveDisabledRef.current = false;
        setGradingError(data.error || 'Submission failed. Please try again.');
        setSubmitting(false);
        return;
      }
      // For non-immediate visibility modes, skip the grading wait — show a pending
      // confirmation screen right away. Grading still runs in the background.
      const visibility = exam?.settings?.resultVisibility || 'immediate';
      if (visibility === 'manual' || visibility === 'after_deadline') {
        const endDate = exam?.settings?.endDate;
        const message = visibility === 'manual'
          ? 'Your exam has been submitted. Your teacher will release results when ready.'
          : endDate
            ? `Your exam has been submitted. Results will be available after the exam closes on ${new Date(endDate).toLocaleString('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}.`
            : 'Your exam has been submitted. Results will be available after the exam window closes.';
        setResultPending({ visible: true, message });
        setSubmitting(false);
        return;
      }

      setGradingPct(5);
      setGradingJobId(data.jobId);
      setSubmitting(false);
    } catch {
      saveDisabledRef.current = false;
      setGradingError('Network error during submission. Please check your connection.');
      setSubmitting(false);
    }
  }, [recordTime, qNum, questions, token, schoolId, schoolExamId, exam]);

  // Keep doSubmitRef current so useProctoring's onBlock can always invoke the latest version
  useEffect(() => {
    doSubmitRef.current = doSubmit;
  }, [doSubmit]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      if (saveProgressRef.current) saveProgressRef.current();
    }, 25000);
    const flushProgress = () => {
      if (saveProgressRef.current) saveProgressRef.current({ beacon: true });
    };
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') flushProgress();
    };
    window.addEventListener('beforeunload', flushProgress);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      mountedRef.current = false;
      clearInterval(intervalId);
      if (saveTimeoutRef.current) clearTimeout(saveTimeoutRef.current);
      flushProgress();
      window.removeEventListener('beforeunload', flushProgress);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  // Auto-submit when timer hits zero
  useEffect(() => {
    if (isExpired && !autoSubmitRef.current && !submitting && !gradingJobId) {
      autoSubmitRef.current = true;
      doSubmit({ automatic: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpired]);

  // SSE grading progress
  useEffect(() => {
    if (!gradingJobId) return;
    const headers = { 'x-access-token': token };
    if (schoolId) headers['x-school-id'] = schoolId;

    const url = `/student/exam-grading-progress/${gradingJobId}`;
    let cancelled = false;

    // Simulated ticker: slowly creep from current progress toward 85%
    // so the bar always appears to be moving while we wait for real SSE events.
    const ticker = setInterval(() => {
      setGradingPct(prev => {
        if (prev >= 85) return prev;
        const step = prev < 30 ? 2 : prev < 60 ? 1 : 0.5;
        return Math.min(prev + step, 85);
      });
    }, 600);

    async function poll() {
      try {
        const res = await fetch(url, { headers });
        if (!res.ok || !res.body) {
          setGradingError('Could not connect to grading service. Please retry.');
          return;
        }
        const reader  = res.body.getReader();
        const decoder = new TextDecoder();
        let buf = '';

        // eslint-disable-next-line no-constant-condition
        while (true) {
          if (cancelled) break;
          const { done, value } = await reader.read();
          if (done) break;
          buf += decoder.decode(value, { stream: true });
          const lines = buf.split('\n');
          buf = lines.pop();
          for (const line of lines) {
            if (!line.startsWith('data:')) continue;
            try {
              const event = JSON.parse(line.slice(5).trim());
              // Update progress bar
              if (event.progress != null) setGradingPct(event.progress);
              // Update status message
              if (event.message) setGradingMsg(event.message);
              else if (event.status && event.status !== 'pending') {
                const readableStatus = {
                  'Starting grading process...': 'Starting grading process…',
                  done: 'Grading complete!',
                  done_cached: 'Grading complete (cached)!',
                  failed: 'Grading failed',
                }[event.status] || event.status;
                setGradingMsg(readableStatus);
              }
              // Terminal states
              if (event.status === 'complete') {
                const attempt = event.attempt || {};
                const examSettings = event.examSettings || {};
                const visibility = examSettings.resultVisibility || 'immediate';
                const endDate = examSettings.endDate;

                setGradingPct(100);

                // Check result visibility setting
                if (visibility === 'manual') {
                  // Teacher needs to manually release results
                  setGradingMsg('✓ Exam submitted successfully!');
                  setResultPending({
                    visible: true,
                    message: 'Your exam has been submitted. Your teacher will release results soon.',
                  });
                  return;
                }

                if (visibility === 'after_deadline') {
                  // Results available only after exam closes
                  if (endDate && new Date() < new Date(endDate)) {
                    const formattedDate = new Date(endDate).toLocaleString('en-US', {
                      month: 'short',
                      day: 'numeric',
                      hour: 'numeric',
                      minute: '2-digit',
                    });
                    setGradingMsg('✓ Exam submitted successfully!');
                    setResultPending({
                      visible: true,
                      message: `Your exam has been submitted. Results will be available after the exam closes on ${formattedDate}.`,
                    });
                    return;
                  }
                  // Deadline passed — show results immediately
                }

                // visibility === 'immediate' or deadline has passed
                onGradingComplete({
                  examId:       attempt.examId   || null,
                  examMetaId:   event.examMetaId || null,
                  schoolExamId,
                  score:        attempt.score,
                  percentage:   attempt.percentage,
                  passed:       attempt.passed,
                });
                return;
              }
              if (event.status === 'failed') {
                setGradingError(attempt?.isRetake
                  ? 'Retake grading failed. Your previous result has been restored. Contact school staff if another retake is needed.'
                  : 'Grading failed. Your answers were saved. You can retry grading below.');
                return;
              }
            } catch { /* parse error, skip line */ }
          }
        }
      } catch {
        if (!cancelled) setGradingError('Connection lost during grading. Please retry.');
      }
    }

    poll();
    return () => { cancelled = true; clearInterval(ticker); };
  }, [gradingJobId, gradingRetry, token, schoolId, onGradingComplete, attempt?.isRetake, schoolExamId]);

  // Countdown to auto-submit when violations exceed the limit
  useEffect(() => {
    if (autoSubmitCountdown === null) return;
    if (autoSubmitCountdown <= 0) {
      if (doSubmitRef.current) doSubmitRef.current({ automatic: true });
      setAutoSubmitCountdown(null);
      return;
    }
    const id = setTimeout(() => setAutoSubmitCountdown(c => c - 1), 1000);
    return () => clearTimeout(id);
  }, [autoSubmitCountdown]);

  // ─── Render: Grading overlay ──────────────────────────────────────────────
  if (gradingJobId || submitting || resultPending?.visible) {
    return (
      <Overlay style={{ alignItems: 'center', justifyContent: 'center' }}>
        <GradingBox className="BubblePopIn">
          {resultPending?.visible ? (
            <>
              <FaCheck style={{ fontSize: 48, color: '#5fbec5' }} />
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: '#0c1e28', fontSize: '1.05rem' }}>
                  Exam Submitted Successfully
                </p>
                <p style={{ margin: '12px 0 0', color: '#607d8b', fontSize: '0.9rem', lineHeight: 1.6, textAlign: 'center' }}>
                  {resultPending.message}
                </p>
              </div>
              <DBtn $primary onClick={() => onGradingComplete({ submitted: true })}>
                Return to Dashboard
              </DBtn>
            </>
          ) : gradingError ? (
            <>
              <p style={{ color: '#dc2626', fontWeight: 600, margin: 0 }}>{gradingError}</p>
              <DBtn
                $primary
                onClick={() => {
                  // If we have a jobId, retry the SSE poll by resetting the error.
                  // Otherwise, trigger a full resubmit.
                  if (gradingJobId) {
                    setGradingError(null);
                    setGradingMsg('Retrying grading…');
                    setGradingPct(5);
                    setGradingRetry(r => r + 1); // re-triggers the SSE useEffect
                  } else {
                    autoSubmitRef.current = false;
                    // Always finalize with the server's last saved snapshot on retry, so a slow
                    // network round-trip past the deadline can never permanently soft-lock submission
                    doSubmit({ automatic: true });
                  }
                }}
              >
                <FaRedo style={{ marginRight: 7 }} /> Retry Grading
              </DBtn>
            </>
          ) : (
            <>
              <Spinner />
              <div>
                <p style={{ margin: 0, fontWeight: 700, color: '#0c1e28', fontSize: '1.05rem' }}>
                  Grading in progress
                </p>
                <p style={{ margin: '6px 0 0', color: '#607d8b', fontSize: '0.85rem' }}>{gradingMsg}</p>
              </div>
              <GradingProgressTrack>
                <GradingProgressFill $pct={gradingPct} />
              </GradingProgressTrack>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#90a4ae' }}>
                This may take up to a minute. Please keep this window open.
              </p>
            </>
          )}
        </GradingBox>
      </Overlay>
    );
  }

  // ── Auto-submit countdown overlay ─────────────────────────────────────────
  if (autoSubmitCountdown !== null) {
    const progress = ((5 - autoSubmitCountdown) / 5) * 100;
    return (
      <FullscreenGate style={{ background: '#1a0a0a', gap: 28 }}>
        <div style={{
          background: 'rgba(220,38,38,0.15)', border: '2px solid #dc2626',
          borderRadius: '50%', width: 72, height: 72,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}>
          <FaExclamationTriangle size={32} style={{ color: '#dc2626' }} />
        </div>
        <div style={{ textAlign: 'center' }}>
          <p style={{ color: '#fff', fontSize: '1.3rem', fontWeight: 800, margin: '0 0 10px' }}>
            Exam Auto-Submission
          </p>
          <p style={{ color: '#fca5a5', fontSize: '0.95rem', margin: 0, maxWidth: 420, lineHeight: 1.6 }}>
            You have exceeded the maximum number of integrity violations.
            Your exam will be automatically submitted in&nbsp;
            <strong style={{ color: '#fff', fontSize: '1.1rem' }}>{autoSubmitCountdown}</strong>
            &nbsp;second{autoSubmitCountdown !== 1 ? 's' : ''}.
          </p>
        </div>
        {/* Progress bar counting up to submission */}
        <div style={{ width: 320, height: 6, background: 'rgba(220,38,38,0.25)', borderRadius: 4, overflow: 'hidden' }}>
          <div style={{
            height: '100%', borderRadius: 4,
            background: '#dc2626',
            width: `${progress}%`,
            transition: 'width 0.9s linear',
          }} />
        </div>
        <p style={{ color: '#6b7280', fontSize: '0.78rem', margin: 0 }}>
          Your answers have been saved and will be submitted.
        </p>
      </FullscreenGate>
    );
  }

  const totalAnswered = Object.values(answers).filter(Boolean).length;
  const totalFlagged  = Object.values(flagged).filter(Boolean).length;

  const warningMessages = {
    1: `⚠ You left the exam window — this has been recorded (${violationCount} flag${violationCount !== 1 ? 's' : ''}).`,
    2: `⚠ ${violationCount} flags recorded. Further violations will be escalated to your teacher.`,
    3: `🚨 Suspicious activity detected. Your teacher has been alerted. Continued violations may result in automatic submission.`,
  };

  // ── Screen-share gate ──────────────────────────────────────────────────────
  if (!screenShareDone) {
    return (
      <FullscreenGate data-proctor-ui="true">
        <MdScreenShare size={48} style={{ color: '#5fbec5' }} />
        <div style={{ color: '#fff', fontSize: '1.2rem', fontWeight: 700, textAlign: 'center' }}>
          Screen monitoring required
        </div>
        <div style={{ color: '#90a4ae', fontSize: '0.88rem', textAlign: 'center', maxWidth: 420, lineHeight: 1.6 }}>
          To ensure exam integrity, your teacher requires your entire screen to be shared
          during this exam. Screenshots may be taken if a violation is detected.
        </div>
        {screenShareWrongSurface && (
          <div style={{
            background: 'rgba(220,38,38,0.10)', border: '1.5px solid #dc2626',
            borderRadius: 10, padding: '10px 18px',
            color: '#f87171', fontSize: '0.83rem', textAlign: 'center', maxWidth: 420,
          }}>
            ⚠ You selected a <strong>window or browser tab</strong> — please click <em>Share Screen &amp; Continue</em> again and choose <strong>Entire Screen</strong> from the share dialog.
          </div>
        )}
        {screenShareDeclined && !screenShareWrongSurface && (
          <div style={{
            background: 'rgba(245,158,11,0.12)', border: '1.5px solid #f59e0b',
            borderRadius: 10, padding: '10px 18px',
            color: '#fbbf24', fontSize: '0.83rem', textAlign: 'center', maxWidth: 400,
          }}>
            ⚠ Screen sharing was declined or cancelled. You may continue without it,
            but your submission might be rejected.
          </div>
        )}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', justifyContent: 'center' }}>
          <FullscreenBtn onClick={handleStartScreenShare}>
            🖥 Share Screen &amp; Continue
          </FullscreenBtn>
          {(screenShareDeclined || screenShareWrongSurface) && (
            <button
              onClick={handleSkipScreenShare}
              style={{
                padding: '14px 24px', borderRadius: 12, border: '1.5px solid #546e7a',
                background: 'transparent', color: '#90a4ae',
                fontSize: '0.9rem', fontWeight: 600, cursor: 'pointer',
              }}
            >
              Continue Without Screen Share
            </button>
          )}
        </div>
      </FullscreenGate>
    );
  }

  // ── Fullscreen gate ────────────────────────────────────────────────────────
  if (!fullscreenReady) {
    return (
      <FullscreenGate>
        <FaShieldAlt size={48} style={{ color: '#5fbec5' }} />
        <div style={{ color: '#fff', fontSize: '1.25rem', fontWeight: 700, textAlign: 'center' }}>
          This exam requires fullscreen mode
        </div>
        <div style={{ color: '#90a4ae', fontSize: '0.9rem', textAlign: 'center', maxWidth: 400 }}>
          For the integrity of this exam, you must remain in fullscreen for the duration.
          Exiting fullscreen will be recorded as a violation.
        </div>
        <FullscreenBtn onClick={handleEnterFullscreen}>
          <MdFullscreen size={22} /> Enter Fullscreen &amp; Begin
        </FullscreenBtn>
      </FullscreenGate>
    );
  }

  return (
    <Overlay>
      {/* ── Proctoring Warning Banner ── */}
      {showWarningBanner && warningLevel > 0 && (
        <WarningBanner $level={warningLevel}>
          <FaExclamationTriangle style={{ flexShrink: 0, marginTop: 2 }} />
          <span>{warningMessages[warningLevel]}</span>
          {warningLevel < 3 && (
            <WarningDismiss $level={warningLevel} onClick={dismissWarning}>✕</WarningDismiss>
          )}
        </WarningBanner>
      )}

      {/* ── Header ── */}
      <Header>
        <ExamTitle>{exam?.title || 'Exam'}</ExamTitle>
        <TimerBadge $low={isLowTime}>
          <PiClockCountdownFill />
          {timeDisplay}
        </TimerBadge>
        <span aria-live="polite" style={{ minWidth: 64, fontSize: '0.72rem', color: saveState === 'error' ? '#b91c1c' : '#607d8b' }}>
          {saveState === 'saving' ? 'Saving...' : saveState === 'saved' ? 'Saved' : saveState === 'error' ? 'Save retry' : ''}
        </span>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <span style={{ fontSize: '0.78rem', color: '#90a4ae' }}>
            {totalAnswered}/{questions.length} answered
          </span>
          {violationCount > 0 && (
            <ViolationBadge>
              <FaExclamationTriangle size={10} /> {violationCount}
            </ViolationBadge>
          )}
          {screenCaptureActive && (
            <span style={{
              display: 'flex', alignItems: 'center', gap: 4,
              fontSize: '0.72rem', fontWeight: 700, color: '#43a047',
              background: 'rgba(67,160,71,0.10)', border: '1px solid rgba(67,160,71,0.35)',
              borderRadius: 99, padding: '2px 8px',
            }}>
              🔴 Screen shared
            </span>
          )}
        </div>
      </Header>

      {/* ── Multi-subject pill tabs ── */}
      {isMultiSubject && (
        <div style={{
          display: 'flex', gap: 6, flexWrap: 'wrap', padding: '8px 24px',
          background: 'rgba(95,190,197,0.06)', borderBottom: '1px solid #e0eaec',
        }}>
          {subjectSections.map((sec, idx) => {
            const secQs = questions.filter(q => (q.subjectSectionIdx ?? 0) === idx);
            const secAnswered = secQs.filter(q => answers[q.question_number] !== undefined && answers[q.question_number] !== '').length;
            const isActive = activeSubjectIdx === idx;
            return (
              <button
                key={idx}
                onClick={() => {
                  activeSubjectIdxRef.current = idx;
                  currentIdxRef.current = 0;
                  setActiveSubjectIdx(idx);
                  setCurrentIdx(0);
                  scheduleAutosave();
                }}
                style={{
                  padding: '5px 14px', borderRadius: 20, border: '1.5px solid',
                  borderColor: isActive ? '#0c7c84' : '#cfd8dc',
                  background: isActive ? '#0c7c84' : '#fff',
                  color: isActive ? '#fff' : '#546e7a',
                  fontWeight: isActive ? 700 : 500, fontSize: '0.82rem', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6, transition: 'all .15s',
                }}
              >
                {sec.subjectName}
                <span style={{
                  background: isActive ? 'rgba(255,255,255,0.25)' : 'rgba(95,190,197,0.15)',
                  color: isActive ? '#fff' : '#0c7c84',
                  borderRadius: 99, padding: '1px 6px', fontSize: '0.72rem', fontWeight: 700,
                }}>{secAnswered}/{secQs.length}</span>
              </button>
            );
          })}
        </div>
      )}

      {/* ── Question ── */}
      <Body>
        <QuestionCard>
          <QuestionMeta>
            <QNum>Question {currentIdx + 1} of {visibleQuestions.length}{isMultiSubject ? ` · ${subjectSections[activeSubjectIdx]?.subjectName}` : ''}</QNum>
            <FlagBtn $flagged={!!flagged[qNum]} onClick={toggleFlag}>
              <FaFlag size={11} /> {flagged[qNum] ? 'Flagged' : 'Flag'}
            </FlagBtn>
          </QuestionMeta>

          {/* Instructions (if any) */}
          {q.instructions && q.instructions.trim() !== '' && (
            <InstructionBlock>{formatLargeText(q.instructions)}</InstructionBlock>
          )}

          {/* Diagram (if any) */}
          {q.diagram && q.diagram.trim() !== '' && (
            <DiagramWrapper>
              <img src={q.diagram} alt="Question diagram" />
            </DiagramWrapper>
          )}

          {/* Theory badge */}
          {(q.questionType === 'theory' || (!q.options || q.options.length === 0)) && (
            <TheoryTypeBadge>✏ Theory — Written Answer</TheoryTypeBadge>
          )}

          <QuestionText>{formatLargeText(q.question_text) || '—'}</QuestionText>

          {/* Theory: free-text answer area */}
          {(q.questionType === 'theory' || (!q.options || q.options.length === 0)) ? (
            <TheoryAnswerWrap>
              <TheoryTextarea
                ref={answerTextareaRef}
                $hasValue={!!(answers[qNum] || '').trim()}
                placeholder="Write your answer here…"
                value={answers[qNum] || ''}
                onChange={e => selectAnswer(e.target.value)}
              />
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <SymTriggerBtn
                  type="button"
                  title="Insert special symbol"
                  onMouseDown={() => {
                    if (answerTextareaRef.current) {
                      lastSelRef.current = {
                        start: answerTextareaRef.current.selectionStart,
                        end: answerTextareaRef.current.selectionEnd,
                      };
                    }
                  }}
                  onClick={() => setSymModalOpen(true)}
                >
                  Ω
                </SymTriggerBtn>
              </div>
              {symModalOpen && (
                <SpecialSymbolsModal
                  onInsert={(symbol) => {
                    const text = answers[qNum] || '';
                    const { start, end } = lastSelRef.current;
                    const newText = text.slice(0, start) + symbol + text.slice(end);
                    selectAnswer(newText);
                    lastSelRef.current = { start: start + symbol.length, end: start + symbol.length };
                    setTimeout(() => {
                      if (answerTextareaRef.current) {
                        answerTextareaRef.current.focus();
                        answerTextareaRef.current.setSelectionRange(start + symbol.length, start + symbol.length);
                      }
                    }, 0);
                  }}
                  onClose={() => setSymModalOpen(false)}
                />
              )}
              <TheoryWordCount>
                {(answers[qNum] || '').trim().split(/\s+/).filter(Boolean).length} words
              </TheoryWordCount>
            </TheoryAnswerWrap>
          ) : (
            <OptionsList>
              <p style={{ margin: 0, fontSize: '0.78rem', color: '#607d8b' }}>
                Click a selected option again to clear your answer.
              </p>
              {(q.options || []).map((opt, i) => (
                <OptionBtn
                  key={i}
                  $selected={answers[qNum] === opt}
                  aria-pressed={answers[qNum] === opt}
                  onClick={() => selectAnswer(answersRef.current[qNum] === opt ? '' : opt)}
                >
                  {opt}
                </OptionBtn>
              ))}
            </OptionsList>
          )}
        </QuestionCard>

        {/* ── Question grid ── */}
        <NavGrid>
          {visibleQuestions.map((qs, i) => {
            const n = qs.question_number || i + 1;
            return (
              <NavDot
                key={n}
                $active={i === currentIdx}
                $answered={!!answers[n]}
                $flagged={!!flagged[n]}
                onClick={() => goTo(i)}
              >
                {i + 1}
              </NavDot>
            );
          })}
        </NavGrid>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 16, justifyContent: 'center', paddingBottom: 24, fontSize: '0.72rem', color: '#90a4ae' }}>
          <span style={{ color: '#0c7c84' }}>■ Current</span>
          <span style={{ color: '#065f46' }}>■ Answered</span>
          <span style={{ color: '#92400e' }}>■ Flagged</span>
          <span>□ Unanswered</span>
        </div>
      </Body>

      {/* ── Footer ── */}
      <Footer>
        <NavBtn onClick={() => goTo(currentIdx - 1)} disabled={currentIdx === 0}>
          <FaChevronLeft size={12} /> Prev
        </NavBtn>

        {currentIdx < visibleQuestions.length - 1 ? (
          <NavBtn $primary onClick={() => goTo(currentIdx + 1)}>
            Next <FaChevronRight size={12} />
          </NavBtn>
        ) : (
          <SubmitBtn onClick={() => setShowConfirm(true)}>
            <FaCheck style={{ marginRight: 6 }} /> Submit Exam
          </SubmitBtn>
        )}

        <SubmitBtn onClick={() => setShowConfirm(true)}>
          Submit
        </SubmitBtn>
      </Footer>

      {/* ── Confirm dialog ── */}
      {showConfirm && (
        <ModalBack>
          <Dialog className="BubblePopIn">
            <DialogTitle>Submit Exam?</DialogTitle>
            <DialogSub>
              You have answered {totalAnswered} of {questions.length} questions.
              {totalFlagged > 0 && ` ${totalFlagged} flagged for review.`}
              {totalAnswered < questions.length && ' Unanswered questions will be marked incorrect.'}
              {' '}This action cannot be undone.
            </DialogSub>
            <DialogBtns>
              <DBtn onClick={() => setShowConfirm(false)}>Review</DBtn>
              <DBtn $primary onClick={doSubmit}>Submit Now</DBtn>
            </DialogBtns>
          </Dialog>
        </ModalBack>
      )}
    </Overlay>
  );
}
